import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  HairlineCard,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FORM4_FIELDS,
  type Form4FieldKey,
  type Form4FieldValues,
  type Form4OverrideEntry,
  type FormSummary,
} from '@munifocus/contracts-gov';
import { formatDateTime } from '../lib/relative-time';
import { useForm4Overrides } from '../lib/form-overrides-api';
import { localizeStatuteRef } from '../lib/file-display';
import { GeneratedDocuments } from './GeneratedDocuments';

function LogEntry({ entry }: { entry: Form4OverrideEntry }) {
  const { t, i18n } = useTranslation();
  // A field can legitimately be overridden from — or to — blank, so an empty
  // value is spelled out rather than rendered as nothing.
  const shown = (value: string) => (value.trim() === '' ? t('gov.form4Override.blank') : value);
  return (
    <Stack spacing={0.25} sx={{ py: 1 }}>
      <Typography variant="body2">{t(`gov.form4Override.fields.${entry.field}`)}</Typography>
      <Typography variant="body2" color="text.secondary">
        {`${shown(entry.originalValue)} → ${shown(entry.newValue)}`}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t('gov.form4Override.by', {
          officer: entry.officerName ?? t('gov.form4Override.unknownOfficer'),
          at: formatDateTime(entry.createdAt, i18n.language),
        })}
      </Typography>
    </Stack>
  );
}

/**
 * Step 5.1 — the Form 4 field sheet.
 *
 * Every field the system can pre-populate from the file record (Stages 1–4)
 * appears here already filled in. Accepting what is shown is the default; the
 * officer can also correct any of it with more current information before the
 * notice is finalized. Because these are the fields an appeal committee or a
 * court will read, each change is logged against the generated notice — original
 * value, new value, officer, timestamp — and the log is shown below the sheet.
 *
 * An override never rewrites the file record. It is scoped to the Form 4 that
 * was produced: evidence of what was served, not a correction at source.
 */
export function Form4Fields({
  fileId,
  forms,
  onGenerate,
  generating,
  generateError,
}: {
  fileId: string;
  forms: FormSummary[];
  /** Receives only the fields the officer changed; the API diffs and logs them. */
  onGenerate: (fieldValues: Form4FieldValues) => void;
  generating: boolean;
  generateError: boolean;
}) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useForm4Overrides(fileId);
  // The officer's edits sit on top of the server's system values rather than
  // replacing them, so a refetch never clobbers unsaved typing and "reset"
  // is simply dropping the edit.
  const [edits, setEdits] = useState<Form4FieldValues>({});

  const system = data?.fields ?? {};
  const entries = data?.entries ?? [];

  const valueOf = (key: Form4FieldKey) => edits[key] ?? system[key] ?? '';
  const isOverridden = (key: Form4FieldKey) =>
    edits[key] !== undefined && edits[key].trim() !== (system[key] ?? '').trim();

  // Until the sheet has loaded, `system` is empty and every field would submit
  // as a blank string — which the API would log as an override of the whole
  // form and print as a blank notice. Generation waits for real values.
  const sheetUnavailable = isLoading || isError;
  // A Notice to Comply without a date to comply by is not a notice anyone can
  // be held to — it would print with the deadline blank. Generation waits for
  // one, whether it came from classification or was entered here.
  const deadlineMissing = !sheetUnavailable && valueOf('complianceDeadline').trim() === '';

  // Only the fields the officer actually changed travel. An override replaces
  // both language columns of the bilingual form with the one string typed here,
  // so sending an untouched field back would print its English value in the
  // French column too.
  const submit = () => {
    if (sheetUnavailable || deadlineMissing) return;
    const overrides: Form4FieldValues = {};
    for (const field of FORM4_FIELDS) {
      if (isOverridden(field.key)) overrides[field.key] = valueOf(field.key);
    }
    onGenerate(overrides);
  };

  const overriddenCount = FORM4_FIELDS.filter((f) => isOverridden(f.key)).length;

  return (
    <Stack spacing={2}>
      <HairlineCard sx={{ p: 2 }}>
        <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
          <Typography variant="overline">{t('gov.escalation.step5.formPreview')}</Typography>
          {overriddenCount > 0 && (
            <Typography variant="caption" color="warning.main">
              {t('gov.form4Override.count', { count: overriddenCount })}
            </Typography>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {t('gov.form4Override.hint')}
        </Typography>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}
        {isError && <Alert severity="error">{t('gov.form4Override.loadError')}</Alert>}

        {!isLoading && !isError && (
          <Stack spacing={2}>
            {FORM4_FIELDS.map((field) => (
              <Stack key={field.key} spacing={0.5}>
                <TextField
                  fullWidth
                  size="small"
                  multiline={field.multiline}
                  minRows={field.multiline ? 2 : undefined}
                  type={field.date ? 'date' : undefined}
                  // `InputLabelProps` is not exposed by the wrapper; date inputs
                  // need the label shrunk or it overlaps the placeholder.
                  slotProps={field.date ? { inputLabel: { shrink: true } } : undefined}
                  label={t(`gov.form4Override.fields.${field.key}`)}
                  value={valueOf(field.key)}
                  onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                />
                <Stack
                  direction="row"
                  sx={{ alignItems: 'baseline', gap: 1, flexWrap: 'wrap', px: 0.5 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {t(`gov.form4Override.sources.${field.source}`)}
                  </Typography>
                  {field.statuteRef && (
                    <Typography variant="caption" color="text.secondary">
                      {localizeStatuteRef(field.statuteRef)}
                    </Typography>
                  )}
                  {isOverridden(field.key) && (
                    <>
                      <Typography variant="caption" color="warning.main">
                        {t('gov.form4Override.overridden')}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() =>
                          setEdits((prev) => {
                            const next = { ...prev };
                            delete next[field.key];
                            return next;
                          })
                        }
                      >
                        {t('gov.form4Override.reset')}
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}

        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {deadlineMissing && (
            <Alert severity="warning">{t('gov.form4Override.deadlineRequired')}</Alert>
          )}
          <GeneratedDocuments
            forms={forms}
            onGenerate={submit}
            generating={generating}
            isError={generateError}
            disabled={sheetUnavailable || deadlineMissing}
            generateLabel={t('gov.escalation.step5.generateForm')}
          />
        </Stack>
      </HairlineCard>

      <HairlineCard sx={{ p: 2 }}>
        <Typography variant="overline">{t('gov.form4Override.logTitle')}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('gov.form4Override.logHint')}
        </Typography>
        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('gov.form4Override.logEmpty')}
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {entries.map((entry) => (
              <LogEntry key={entry.id} entry={entry} />
            ))}
          </Stack>
        )}
      </HairlineCard>
    </Stack>
  );
}
